const defaultStatus = {
  key: null,
  color: '#ededed'
};

/**
 * Collection of utility helpers for working with objects, arrays, and query parameters.
 * @namespace objectHelpers
 * @example
 * import { objectHelpers } from './object.helpers';
 * objectHelpers.isEmptyObject({});
 */
export const objectHelpers = {
  /**
   * Returns true if every value in the object is null, undefined, or an empty string.
   * @param {Object} obj - The object to check.
   * @returns {boolean} True when all values are empty.
   * @example
   * objectHelpers.allValuesEmpty({ a: null, b: '' }); // true
   */
  allValuesEmpty: (obj) => Object.values(obj).every(val => val === null || val === undefined || val === ''),
  /**
   * Returns true if two arrays are deeply equal by comparing their JSON representations.
   * @param {Array} arr1 - First array to compare.
   * @param {Array} arr2 - Second array to compare.
   * @returns {boolean} True when both arrays are structurally identical, false on mismatch or serialization error.
   * @example
   * objectHelpers.arraysIsEqual([1, 2], [1, 2]); // true
   */
  arraysIsEqual: (arr1, arr2) => {
    try {
      return JSON.stringify(arr1) === JSON.stringify(arr2)
    } catch (e) {
      return false
    }
  },
  /**
   * Safely reads a property from an object, returning a default value when the property is absent.
   * @param {Object} obj - Source object.
   * @param {string} propName - Property name to retrieve.
   * @param {*} [defaultValue=''] - Value returned when the property does not exist on obj.
   * @returns {*} The property value or defaultValue.
   * @example
   * objectHelpers.getObjProp({ foo: 42 }, 'foo'); // 42
   */
  getObjProp: (obj, propName, defaultValue = '') => {
    if (obj && obj.hasOwnProperty(propName)) {
      return obj[propName]
    } else {
      return defaultValue
    }
  },
  /**
   * Finds the label for a given value within an options list, returning '-' when the option is not found.
   * @param {Array<{value: *, label: string}>} [options=[]] - Options array to search.
   * @param {*} value - Value to look up.
   * @returns {string|undefined} Matching label, '-' if not found, or undefined when value is null/undefined.
   * @example
   * objectHelpers.getOptionLabel([{ value: 1, label: 'One' }], 1); // 'One'
   */
  getOptionLabel: (options = [], value) => value !== null && value !== undefined ? options.find(o => o.value === value)?.label ?? '-' : undefined,
  /**
   * Returns the status object for a given key from a status-type map, falling back to defaultStatus.
   * @param {string} statusKey - Key identifying the status.
   * @param {Object} statusType - Map of status keys to status objects.
   * @returns {{key: string|null, color: string}} Matching status object or the default status.
   * @example
   * objectHelpers.getStatus('active', { active: { key: 'Active', color: '#0f0' } });
   */
  getStatus: (statusKey, statusType) => statusType.hasOwnProperty(statusKey) ? statusType[statusKey] : defaultStatus,
  /**
   * Returns true when the given object has no own enumerable properties.
   * @param {Object} obj - The object to check.
   * @returns {boolean} True when obj is equivalent to an empty object.
   * @example
   * objectHelpers.isEmptyObject({}); // true
   */
  isEmptyObject: (obj) => objectHelpers.arraysIsEqual(obj, {}),
  /**
   * Returns true when a query object carries no filter values.
   *
   * A key counts when its value is actually present, which is not the same as its having a length.
   * This used to test `query[key] && query[key].length`, so every NUMERIC and BOOLEAN filter was
   * invisible to it: `{year: 2026, month: 8}` from the period selector read as no filter at all,
   * as did `{is_incoming: true}` and `{low_value: 0}` (H1MVP-4710). Callers that needed one of
   * those had to special-case it themselves, which is how the reading of a shared helper drifted
   * per call site.
   *
   * Empty strings and empty arrays are absent — they are what a cleared control leaves behind.
   * `0` and `false` are present: someone chose them.
   *
   * @param {Object|null|undefined} query - The query object to inspect.
   * @returns {boolean} True when the query is empty or falsy.
   * @example
   * objectHelpers.isEmptyQuery({ search: '' });        // true
   * objectHelpers.isEmptyQuery({ status: [] });        // true
   * objectHelpers.isEmptyQuery({ year: 2026 });        // false
   * objectHelpers.isEmptyQuery({ has_invoice: false }); // false
   */
  isEmptyQuery: (query) => (query
    ? !Object.keys(query).some((key) => {
      const value = query[key];
      if (!objectHelpers.isValidValue(value)) return false;
      return !(Array.isArray(value) && value.length === 0);
    })
    : true),
  /**
   * Returns true when the value is null or undefined (is Not a Value).
   * @param {*} value - Value to test.
   * @returns {boolean} True when value is null or undefined.
   * @example
   * objectHelpers.isNaV(null); // true
   */
  isNaV: (value) => [null, undefined].includes(value),
  /**
   * Returns true when a value is defined, non-null, and non-empty-string.
   * @param {*} value - Value to validate.
   * @returns {boolean} True when value is considered valid.
   * @example
   * objectHelpers.isValidValue('hello'); // true
   */
  isValidValue: (value) => value !== undefined && value !== null && value !== '',
  /**
   * Generates an inclusive array of integers from start to end.
   * @param {number} start - The first integer in the range.
   * @param {number} end - The last integer in the range (inclusive).
   * @returns {number[]} Array of integers from start to end.
   * @example
   * objectHelpers.range(1, 5); // [1, 2, 3, 4, 5]
   */
  range: (start, end) => (new Array(end - start + 1)).fill(undefined).map((_, i) => i + start),
  /**
   * Returns a new object with all string values trimmed of leading and trailing whitespace.
   * @param {Object} [fields={}] - Object whose string values should be trimmed.
   * @returns {Object} New object with identical keys and trimmed string values.
   * @example
   * objectHelpers.trimAllValues({ name: '  Alice  ', age: 30 }); // { name: 'Alice', age: 30 }
   */
  trimAllValues: (fields = {}) => {
    return Object.keys(fields).reduce((acc, key) => {
      const value = fields[key];
      acc[key] = typeof value === 'string' ? value.trim() : value;
      return acc;
    }, {});
  },
  /**
   * Returns a new object containing only entries whose values pass the isValidValue check.
   * @param {Object} obj - Source object to filter.
   * @returns {Object} New object excluding null, undefined, and empty-string values.
   * @example
   * objectHelpers.omitInvalidValues({ a: 1, b: null, c: '' }); // { a: 1 }
   */
  omitInvalidValues: (obj) => Object.fromEntries(Object.entries(obj).filter(([_, value]) => objectHelpers.isValidValue(value))),
  /**
   * Converts a URLSearchParams instance into a plain object, parsing numeric values and collecting repeated keys into arrays.
   * @param {URLSearchParams} searchParams - The URL search params instance to convert.
   * @returns {Object} Plain object with parsed values; repeated keys become arrays.
   * @example
   * objectHelpers.getSearchParams(new URLSearchParams('page=2&tag=a&tag=b')); // { page: 2, tag: ['a', 'b'] }
   */
  getSearchParams: (searchParams) => {
    const paramsObject = {};

    const parseValue = (value) => {
      if (!isNaN(value) && value.trim() !== "") {
        const parsed = value.includes('.') ? parseFloat(value) : parseInt(value, 10);
        // Large ids (e.g. 19-digit transaction/account ids) exceed Number.MAX_SAFE_INTEGER,
        // so parsing them loses precision (…471104 -> …471000). Keep the original string
        // whenever the parsed number doesn't stringify back to the exact input, so ids
        // survive a getSearchParams -> setSearchParams round-trip intact.
        return String(parsed) === value.trim() ? parsed : value;
      }
      return value;
    };

    searchParams.forEach((value, key) => {
      const parsedValue = parseValue(value);

      if (paramsObject[key]) {
        // If key already exists, convert it to an array (if not already) and add the new value
        paramsObject[key] = Array.isArray(paramsObject[key])
          ? [...paramsObject[key], parsedValue]
          : [paramsObject[key], parsedValue];
      } else {
        // If key doesn't exist, just assign the parsed value
        paramsObject[key] = parsedValue;
      }
    });

    return paramsObject;
  },
  /**
   * Returns a shallow copy of an object with the specified keys removed.
   * @param {Object} obj - Source object.
   * @param {string[]} [excludeKeys=[]] - Array of key names to omit from the result.
   * @returns {Object} New object without the excluded keys.
   * @example
   * objectHelpers.removeKeysFromObject({ a: 1, b: 2, c: 3 }, ['b', 'c']); // { a: 1 }
   */
  removeKeysFromObject: (obj, excludeKeys = []) => Object.fromEntries(Object.entries(obj).filter(([key]) => !excludeKeys.includes(key))),

  /**
   * Returns true when all specified fields exist on the object with non-empty, non-null values.
   * @param {Object} obj - Object to validate.
   * @param {string[]} [requiredFields=[]] - List of property names that must be present and non-empty.
   * @returns {boolean} True when every required field is filled.
   * @throws {Error} When requiredFields is not an array.
   * @example
   * objectHelpers.areAllRequiredFieldsFilled({ name: 'Alice', age: 30 }, ['name', 'age']); // true
   */
  areAllRequiredFieldsFilled: (obj, requiredFields = []) => {
    if (!Array.isArray(requiredFields)) {
      throw new Error('requiredFields must be an array');
    }
    return requiredFields.every(field => obj.hasOwnProperty(field) && obj[field] !== undefined && obj[field] !== null && obj[field] !== '');
  },

  /**
   * Merges URLSearchParams entries with a filter query object, omitting any invalid values from the result.
   * @param {Object} [params={}] - Parameters object.
   * @param {URLSearchParams} params.searchParams - Current URL search params.
   * @param {Object} [params.filterQuery={}] - Additional filter key-value pairs that override search params.
   * A key that repeats in the URL (`?tag=a&tag=b`) comes back as an array, so no value is lost when the result is written back with `setSearchParams`.
   * @returns {Object} Merged and filtered plain object of query parameters.
   * @example
   * objectHelpers.getSearchQueryParams({ searchParams: new URLSearchParams('page=1'), filterQuery: { status: 'active' } });
   * objectHelpers.getSearchQueryParams({ searchParams: new URLSearchParams('tag=a&tag=b'), filterQuery: { page: 2 } }); // { tag: ['a', 'b'], page: 2 }
   */
  getSearchQueryParams: ({searchParams, filterQuery = {}} = {}) => {
    const current = {};
    searchParams.forEach((value, key) => {
      current[key] = Object.hasOwn(current, key) ? [].concat(current[key], value) : value;
    });

    return Object.fromEntries(
      Object.entries({...current, ...filterQuery}).filter(([_, value]) => objectHelpers.isValidValue(value))
    );
  },
  /**
   * Sums the numeric values of a specific property across all objects in an array.
   * @param {Object[]} [arr=[]] - Array of objects to iterate over.
   * @param {string} property - Property name whose numeric value is accumulated.
   * @returns {number} Sum of all valid numeric property values; non-numeric entries contribute 0.
   * @example
   * objectHelpers.summarizeNumericProperty([{ amount: 10 }, { amount: 5.5 }], 'amount'); // 15.5
   */
  summarizeNumericProperty: (arr = [], property) => {
    return arr.reduce((sum, obj) => {
      const value = parseFloat(obj[property]);
      return sum + (isNaN(value) ? 0 : value);
    }, 0);
  },
  /**
   * Returns true when a value is considered empty: null, undefined, blank string, empty array, or an array whose every element is empty.
   * @param {*} value - Value to check.
   * @returns {boolean} True when the value is empty.
   * @example
   * objectHelpers.isEmptyValue(null);        // true
   * objectHelpers.isEmptyValue('');          // true
   * objectHelpers.isEmptyValue([]);          // true
   * objectHelpers.isEmptyValue([null, '']); // true
   * objectHelpers.isEmptyValue('hello');     // false
   */
  isEmptyValue: (value) => {
    if (value === null || value === undefined) return true;
    if (typeof value === 'string') return value.trim() === '';
    if (Array.isArray(value)) return value.length === 0 || value.every(objectHelpers.isEmptyValue);
    return false;
  },

  /**
   * Returns true when a value has no meaningful content for display purposes.
   * Treats null, undefined, blank strings, empty arrays, and zero (numeric or string "0") as blank.
   * Useful for determining whether a loading skeleton should be shown instead of a value.
   * @param {*} value - Value to check.
   * @returns {boolean} True when the value is blank or zero.
   * @example
   * objectHelpers.isBlankValue(null);   // true
   * objectHelpers.isBlankValue('');     // true
   * objectHelpers.isBlankValue([]);     // true
   * objectHelpers.isBlankValue(0);      // true
   * objectHelpers.isBlankValue('0');    // true
   * objectHelpers.isBlankValue('foo');  // false
   * objectHelpers.isBlankValue([1]);    // false
   */
  isBlankValue: (value) => {
    if (!objectHelpers.isValidValue(value)) return true;
    if (Array.isArray(value)) return value.length === 0;
    return Number(value) === 0;
  },
  /**
   * Splits an array into consecutive chunks of the given size.
   * @param {Array} array - The source array to split.
   * @param {number} size - Maximum number of items per chunk. Must be > 0.
   * @returns {Array[]} Array of chunks; the last chunk may be smaller than size.
   * @example
   * objectHelpers.chunkArray([1,2,3,4,5], 2); // [[1,2],[3,4],[5]]
   * objectHelpers.chunkArray([], 50);          // []
   */
  chunkArray: (array, size) => {
    if (!size || size <= 0) return [array];
    const chunks = [];
    for (let i = 0; i < array.length; i += size) {
      chunks.push(array.slice(i, i + size));
    }
    return chunks;
  },
}
